import {useEffect} from "react";

const TARGET_SELECTOR = [
  ".main-button",
  ".project-card",
  ".experience-card",
  ".experience-card-dark",
  ".software-skill-inline",
  ".skills-list-item",
  ".header .menu a[href^='#']",
  ".nav-link",
  ".icon-button",
  ".greeting-highlights li",
  ".greeting-stats li",
  ".project-link",
  ".exp-nav-btn",
  ".education-card",
  ".contact-detail",
  ".contact-detail-email",
  ".contact-resume-link",
  ".footer-top-link",
  ".greeting-ghost-link",
  ".download-link-button",
  ".projects-mobile-dot",
  ".section-eyebrow",
  ".lottie-frame"
].join(",");

export default function useCursorInteract(enabled, glowRef) {
  useEffect(() => {
    if (!enabled) return undefined;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (reduced || !fine) return undefined;

    let active = null;
    let raf = 0;
    let mx = 0;
    let my = 0;

    const applyMagnetic = () => {
      if (!active) return;
      const rect = active.getBoundingClientRect();
      const x = (mx - rect.left) / rect.width - 0.5;
      const y = (my - rect.top) / rect.height - 0.5;
      const pull = active.classList.contains("main-button") ? 10 : 7;
      active.style.setProperty("--tilt-x", `${(-y * 5).toFixed(2)}deg`);
      active.style.setProperty("--tilt-y", `${(x * 6).toFixed(2)}deg`);
      active.style.setProperty("--pull-x", `${(x * pull).toFixed(2)}px`);
      active.style.setProperty("--pull-y", `${(y * pull).toFixed(2)}px`);
      active.style.setProperty(
        "--spot-x",
        `${((mx - rect.left) / rect.width) * 100}%`
      );
      active.style.setProperty(
        "--spot-y",
        `${((my - rect.top) / rect.height) * 100}%`
      );
    };

    const resetActive = () => {
      if (!active) return;
      active.classList.remove("is-cursor-active");
      active.style.removeProperty("--tilt-x");
      active.style.removeProperty("--tilt-y");
      active.style.removeProperty("--pull-x");
      active.style.removeProperty("--pull-y");
      active.style.removeProperty("--spot-x");
      active.style.removeProperty("--spot-y");
      active = null;
      glowRef?.current?.classList.remove("is-on-target");
    };

    const onMove = e => {
      mx = e.clientX;
      my = e.clientY;

      const el = e.target.closest(TARGET_SELECTOR);
      if (!el) {
        if (active) resetActive();
        return;
      }

      if (el !== active) {
        if (active) resetActive();
        active = el;
        active.classList.add("is-cursor-active");
        glowRef?.current?.classList.add("is-on-target");
      }

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(applyMagnetic);
    };

    document.addEventListener("mousemove", onMove, {passive: true});

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("mousemove", onMove);
      resetActive();
    };
  }, [enabled, glowRef]);
}
